import type { ArtboardSize } from './types';

/** Social formats a vendor actually posts in, at the platforms' native sizes. */
export interface ArtboardPreset {
    id: string;
    /** i18n key under DESIGN_EDITOR.SIZE_* */
    labelKey: string;
    ratio: string;
    w: number;
    h: number;
}

export const ARTBOARD_PRESETS: ArtboardPreset[] = [
    { id: 'post', labelKey: 'DESIGN_EDITOR.SIZE_POST', ratio: '1:1', w: 1080, h: 1080 },
    { id: 'portrait', labelKey: 'DESIGN_EDITOR.SIZE_PORTRAIT', ratio: '4:5', w: 1080, h: 1350 },
    { id: 'story', labelKey: 'DESIGN_EDITOR.SIZE_STORY', ratio: '9:16', w: 1080, h: 1920 },
    { id: 'landscape', labelKey: 'DESIGN_EDITOR.SIZE_LANDSCAPE', ratio: '16:9', w: 1920, h: 1080 },
    { id: 'pinterest', labelKey: 'DESIGN_EDITOR.SIZE_PINTEREST', ratio: '2:3', w: 1000, h: 1500 },
];

/** Longest side of an artboard derived from a picture — bounded for memory and upload size. */
export const MAX_IMAGE_ARTBOARD = 2048;

export function artboardFromImage(w: number, h: number): ArtboardSize {
    if (!w || !h) return { w: 1080, h: 1080 };
    const k = Math.min(1, MAX_IMAGE_ARTBOARD / Math.max(w, h));
    return { w: Math.round(w * k), h: Math.round(h * k) };
}

export function matchPreset(size: ArtboardSize): ArtboardPreset | undefined {
    return ARTBOARD_PRESETS.find((p) => p.w === size.w && p.h === size.h);
}

const COMMON_RATIOS: Array<[number, number]> = [[1, 1], [4, 5], [5, 4], [3, 4], [4, 3], [2, 3], [3, 2], [9, 16], [16, 9], [1, 2], [2, 1]];

/** Ratio label for any size, e.g. 1536×1024 → "3:2", 816×1024 → "4:5" (within 2%). */
export function ratioLabel(size: ArtboardSize): string {
    const r = size.w / size.h;
    const near = COMMON_RATIOS.find(([a, b]) => Math.abs(r / (a / b) - 1) < 0.02);
    if (near) return `${near[0]}:${near[1]}`;
    const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
    const g = gcd(Math.round(size.w), Math.round(size.h)) || 1;
    const a = Math.round(size.w / g);
    const b = Math.round(size.h / g);
    if (a > 50 || b > 50) return (size.w / size.h).toFixed(2);
    return `${a}:${b}`;
}
